<script setup lang="ts">
import { computed } from 'vue'
import { useScreen } from '@/composables/useScreen'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()
const squirrelsImage = `url('${asset('img/student-smartbook-squirrels.png')}')`
const tasksImage = `url('${asset('img/student-smartbook-tasks.png')}')`
const shopImage = `url('${asset('img/student-smartbook-shop.png')}')`

interface SmartBook {
  unlocked?: boolean
  demo?: boolean
  next_award?: { progress: number }
  [key: string]: unknown
}

const props = defineProps<{
  unitName?: string
  studentId?: number
  book: SmartBook
  hasTails?: boolean
  demoUrl?: string
}>()

const progressAsPercent = computed(() => {
  if (props.book.next_award) return Math.floor(props.book.next_award.progress)
  return 100
})

const isNotAvailable = computed(() => !props.book.unlocked)
const isDemo = computed(() => !!props.book.demo)

const { isMobile } = useScreen()
</script>

<template>
  <section class="smart-book-card" :class="{ 'smart-book-card__is-demo': isDemo }">
    <div class="smart-book-card__full-width d-flex align-center">
      <h3 class="text-title-bold">{{ unitName }}</h3>

      <p v-if="isDemo" class="text-caption">
        This is a demo version. You can buy a «Smart book» in the parent’s cabinet.
      </p>

      <span class="flex-spacer" />

      <div class="d-flex align-center">
        <span class="text-body-bold">{{ book.star_count }}</span>
        <v-icon class="ml-1 ml-d-2" size="32">3d-star</v-icon>
      </div>
    </div>

    <div class="smart-book-card__card d-flex" :class="{ 'flex-column': isMobile }">
      <div class="smart-book-card__card-section">
        <div class="smart-book-card__img smart-book-card__img_squirrels" />

        <v-button
          v-if="isDemo"
          mode="warn outlined"
          :href="`/student/smartbook_info?student_id=${studentId}`"
          target="_blank"
          class="mb-1"
        >
          <v-icon class="mr-half">mdi-information</v-icon>
          More info
        </v-button>

        <v-button mode="success filled" :href="book.videolessons_url">
          <v-icon class="mr-half">student-smartbook-open</v-icon>
        </v-button>
      </div>
    </div>

    <div class="smart-book-card__card flex-column" :class="{ 'smart-book-card__card_disabled': isNotAvailable }">
      <div class="smart-book-card__img smart-book-card__img_tasks" />

      <v-button :mode="isNotAvailable ? 'inactive' : 'error filled'" :href="book.workbook_url">
        <v-icon class="mr-half">mdi-heart</v-icon>
      </v-button>
    </div>

    <div class="smart-book-card__card flex-column" :class="{ 'smart-book-card__card_disabled': isNotAvailable }">
      <div class="smart-book-card__img smart-book-card__img_shop" />

      <v-button :mode="isNotAvailable ? 'inactive' : 'warn filled'" :href="book.avatar_url + '&shop=true'">
        <v-icon class="mr-half">mdi-cart</v-icon>
      </v-button>
    </div>

    <div class="smart-book-card__card flex-column" :class="{ 'smart-book-card__card_disabled': isNotAvailable }">
      <div
        class="smart-book-card__img"
        :style="{ backgroundImage: `url('${asset(`img/student-smartbook-avatar-${book.scene}.png`)}')` }"
      />

      <v-button :mode="isNotAvailable ? 'inactive' : 'primary filled'" :href="book.avatar_url">
        <v-icon class="mr-half">mdi-home</v-icon>
      </v-button>
    </div>

    <div class="smart-book-card__full-width d-flex pt-2">
      <div class="smart-book-card__progress-bar flex-spacer">
        <div class="smart-book-card__progress-bar-track" :style="{ width: `${progressAsPercent}%` }">
          {{ progressAsPercent }}%
        </div>
      </div>

      <img width="44" height="44" :src="asset('img/student-smartbook-gift.png')" />
    </div>
  </section>
</template>

<style lang="scss">
.smart-book-card {
  border-radius: 16px;
  background: #faf5eb;

  padding: 16px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @include mobile {
    display: flex;
    flex-direction: column;
  }

  & .v-button._warn._outlined:not(:hover) {
    background: white;
  }

  & .v-button._warn._filled {
    color: white;
  }

  &__full-width {
    grid-column: 1 / 5;
    gap: 16px;

    @include mobile {
      align-items: stretch !important;
      flex-direction: column;
      gap: 8px;
    }
  }

  &__progress-bar {
    position: relative;
    border-radius: 999px;
    height: 36px;

    border: 4px solid white;
    box-shadow: 4px 4px 8px 0px rgba(122, 122, 122, 0.44);
    background: linear-gradient(180deg, #e3e3e3 0%, rgba(245, 245, 245, 0) 100%);

    margin-right: 24px;

    &-track {
      background: linear-gradient(180deg, #46caf3 0%, #53a1fe 62.5%, #4e5cde 97.92%);
      height: 100%;
      border-radius: 999px;

      text-align: right;
      font-weight: bold;
      padding: 2px 4px 2px 2px;
      color: white;

      min-width: 32px;

      overflow: hidden;
      white-space: nowrap;
    }
  }

  &__card {
    padding: 16px;
    display: flex;
    border-radius: 8px;
    background: white;
    position: relative;
    box-shadow: 0px 8px 16px 0px #0000001a;

    min-height: 212px;

    &_disabled {
      opacity: 0.5;
    }
  }

  &__card-section {
    display: flex;
    flex-direction: column;
    flex: 1 0 calc(50% - 8px);

    @include desktop {
      &:not(:last-child) {
        margin-right: 16px;
      }
    }
  }

  &__img {
    flex: 1 0 auto;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;

    &_squirrels {
      background-image: v-bind(squirrelsImage);
    }

    &_tasks {
      background-image: v-bind(tasksImage);
    }

    &_shop {
      background-image: v-bind(shopImage);
    }

    @include mobile {
      height: 140px;
    }
  }
}
</style>
